import { queryOptions, useQuery } from '@tanstack/react-query';
import { Activity, Check, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';

interface ActivityItem {
  id: string;
  action: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

const activityQueryOptions = queryOptions({
  queryKey: ['account', 'my-activity'] as const,
  queryFn: () => api<{ items: ActivityItem[] }>(`/api/admin/auth/my-activity`).then((d) => d.items),
  staleTime: 30_000,
});

const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'เข้าสู่ระบบสำเร็จ',
  'auth.login_failed': 'เข้าสู่ระบบล้มเหลว',
  'auth.login_2fa_required': 'รอ 2FA',
  'auth.2fa_verified': 'ยืนยัน 2FA สำเร็จ',
  'auth.2fa_failed': 'ยืนยัน 2FA ล้มเหลว',
  'auth.logout': 'ออกจากระบบ',
};

function isFailedAction(action: string): boolean {
  return action.endsWith('_failed');
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} วันที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Per-user activity feed surfaced on /account so the owner can spot
 * unauthorized access ("did I log in from this IP at 3am?"). Reads
 * from a dedicated endpoint that bypasses audit.read — accessing
 * one's own auth history is always allowed.
 */
export function LoginActivitySection() {
  const query = useQuery(activityQueryOptions);
  const items = query.data ?? [];

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-slate-50">
        <Activity className="h-4 w-4 text-slate-400" />
        กิจกรรมการเข้าสู่ระบบ
      </h3>
      <p className="mb-4 text-xs text-slate-500">
        เห็นกิจกรรมล้มเหลวที่ไม่ใช่คุณ? เปลี่ยนรหัสผ่านและตรวจสอบ 2FA ทันที
      </p>
      {query.isLoading ? (
        <p className="text-sm text-slate-500">กำลังโหลด...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">ยังไม่มีกิจกรรม</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((a) => {
            const failed = isFailedAction(a.action);
            const Icon = failed ? ShieldAlert : Check;
            const iconClass = failed ? 'text-amber-500' : 'text-emerald-500';
            return (
              <li key={a.id} className="flex items-start gap-3 py-2 text-xs">
                <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconClass}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-slate-700 dark:text-slate-200">
                    {ACTION_LABEL[a.action] ?? a.action}
                  </div>
                  {a.ipAddress && (
                    <div className="font-mono text-[10px] text-slate-400">
                      IP {a.ipAddress}
                      {a.userAgent && ` · ${shortenUa(a.userAgent)}`}
                    </div>
                  )}
                </div>
                <span className="shrink-0 text-[11px] text-slate-400">
                  {formatRelative(a.createdAt)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Compact UA string: take browser+OS hint, drop the version-soup. */
function shortenUa(ua: string): string {
  // Look for the parenthesized OS/platform segment + the last browser-y
  // token. The full UA is noisy; users only really want "Chrome/Mac" etc.
  const browser = /(Edg|Chrome|Firefox|Safari)\/[\d.]+/.exec(ua)?.[1] ?? ua.split(/\s/)[0] ?? '';
  const platform = /\(([^)]+)\)/.exec(ua)?.[1]?.split(';')[0]?.trim() ?? '';
  return [browser, platform].filter(Boolean).join(' / ');
}
